<template>
    <div class="d-flex">
        <div class="subjects-container">
            <v-row no-gutters>
                <v-col cols="12">
                    <div v-if="isLoading" class="text-center mb-12">
                        <!-- Skeleton para los filtros -->
                        <v-card class="mb-6 rounded-0" flat color="transparent">
                            <v-card-text class="pt-2 px-0">
                                <div class="d-flex align-center">
                                    <v-skeleton-loader type="image" width="500" height="40" class="mr-4"
                                        style="background-color: white; border-radius: 13px;"></v-skeleton-loader>
                                    <v-spacer></v-spacer>
                                    <v-skeleton-loader type="image" width="80" height="40" class="mr-4"
                                        style="background-color: white; border-radius: 13px;"></v-skeleton-loader>
                                    <v-skeleton-loader type="image" width="200" height="40"
                                        style="background-color: white; border-radius: 13px;"></v-skeleton-loader>
                                </div>
                            </v-card-text>
                        </v-card>

                        <!-- Skeleton para el grid de asignaturas -->
                        <v-row class="mt-7">
                            <v-col cols="12" sm="6" md="4" v-for="n in 6" :key="n" class="pa-2">
                                <v-skeleton-loader class="mx-auto rounded-lg" type="card"
                                    height="80"></v-skeleton-loader>
                            </v-col>
                        </v-row>
                    </div>

                    <div v-else-if="error" class="text-center my-12">
                        <v-alert type="error" outlined>
                            {{ error }}
                        </v-alert>
                        <v-btn color="primary" class="mt-4" @click="fetchSubjects">
                            Intentar de nuevo
                        </v-btn>
                    </div>

                    <v-row v-else>
                        <v-col cols="12">
                            <!-- Filtros y búsqueda -->
                            <v-card class="mb-6 rounded-0" flat color="transparent">
                                <v-card-text class="pt-2 pb-0 px-0">
                                    <div class="d-flex align-center">
                                        <v-text-field v-model="searchQuery" label="Buscar asignaturas"
                                            prepend-inner-icon="$search" dense solo flat hide-details clearable
                                            class="search-field" color="#373837" style="border-radius: 13px;"
                                            @input="filterSubjects"></v-text-field>

                                        <v-spacer></v-spacer>
                                        <v-btn-toggle class="mr-4 rounded-xl"
                                            style="background-color: #ededed6e; padding: 4px 5px; height: 40px;">
                                            <v-btn small :color="viewMode === 'grid' ? 'white' : 'transparent'"
                                                height="32" @click="viewMode = 'grid'" :ripple="false"
                                                :style="viewMode === 'grid' ? 'outline: rgb(218 210 181 / 26%) solid 1px !important' : ''"
                                                style="margin-right: 2px; border: 0 !important">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                                                    viewBox="0 0 24 24" fill="none"
                                                    :stroke="viewMode === 'grid' ? '#1E1E1E' : '#4a4a4a'"
                                                    stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                                                    class="lucide lucide-layout-grid-icon lucide-layout-grid">
                                                    <rect width="7" height="7" x="3" y="3" rx="1" />
                                                    <rect width="7" height="7" x="14" y="3" rx="1" />
                                                    <rect width="7" height="7" x="14" y="14" rx="1" />
                                                    <rect width="7" height="7" x="3" y="14" rx="1" />
                                                </svg>
                                            </v-btn>
                                            <v-btn small :color="viewMode === 'list' ? 'white' : 'transparent'"
                                                height="32"
                                                :style="viewMode === 'list' ? 'outline: rgb(218 210 181 / 26%) solid 1px !importante' : ''"
                                                @click="viewMode = 'list'" :ripple="false"
                                                style="margin-left: 1px; border: 0 !important" depressed>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                                                    viewBox="0 0 24 24" fill="none"
                                                    :stroke="viewMode === 'list' ? '#1E1E1E' : '#4a4a4a'"
                                                    stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                                                    class="lucide lucide-align-justify-icon lucide-align-justify">
                                                    <path d="M3 12h18" />
                                                    <path d="M3 18h18" />
                                                    <path d="M3 6h18" />
                                                </svg>
                                            </v-btn>
                                        </v-btn-toggle>
                                        <v-select v-model="sortOption" :items="sortOptions" label="Ordenar por"
                                            max-width="200" style="max-width: 200px; border-radius: 13px;"
                                            class="font-rubik select-sort" background-color="#ededed6e" dense
                                            color="#373837" hide-details solo flat outlined
                                            content-class="sort-select rounded-xl" @change="filterSubjects"></v-select>


                                        <v-tooltip bottom content-class="rounded-lg" background-color="#ededed6e"
                                            color="#373837">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-btn 
                                                v-if="$root.currentUser && $root.currentUser.userId"
                                                v-on="on" small color="#ededed6e" height="40" width="40"
                                                    @click="editSubjects" class="ml-3"
                                                    style="min-width: 40px; min-height: 40px;">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                                                        viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                                        stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                                                        class="lucide lucide-pencil-icon lucide-pencil">
                                                        <path
                                                            d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
                                                        <path d="m15 5 4 4" />
                                                    </svg>
                                                </v-btn>

                                            </template>
                                            Editar asignaturas
                                        </v-tooltip>

                                    </div>
                                </v-card-text>
                            </v-card>

                            <!-- Lista de asignaturas -->
                            <v-card class="subject-table-container rounded-xl"
                                :class="viewMode === 'list' ? 'pa-3' : ''" flat
                                :color="viewMode === 'list' ? 'white' : 'transparent'">
                                <!-- Vista de lista -->
                                <v-data-table v-if="viewMode === 'list'" :headers="headers" :items="filteredSubjects"
                                    :items-per-page="-1" hide-default-footer class="elevation-0 subject-table"
                                    @click:row="viewSubject" :item-class="() => 'clickable-row'">
                                    <template v-slot:item.icon="{ item }">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                                            viewBox="0 0 24 24" fill="#4DD453"
                                            style="filter: drop-shadow(0 1px 0 #58a700);" stroke-width="2"
                                            stroke-linecap="round" stroke-linejoin="round"
                                            class="lucide lucide-folder mt-1">
                                            <path
                                                d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />
                                        </svg>
                                    </template>

                                    <template v-slot:item.name="{ item }">
                                        <div>
                                            <div style="font-size: 15px;" class="font-rubik font-weight-medium">{{
                                                item.name
                                                }}</div>
                                        </div>
                                    </template>

                                </v-data-table>

                                <!-- Botón para agregar más asignaturas - Vista Lista -->
                                <div v-if="viewMode === 'list'" class="d-flex justify-center mt-4 mb-2">
                                    <v-btn 
                                        color="primary" 
                                        outlined 
                                        @click="editSubjects"
                                        class="rounded-lg"
                                        style="text-transform: none; font-family: 'Rubik', sans-serif;">
                                        <v-icon left small>mdi-plus</v-icon>
                                        Añadir más asignaturas
                                    </v-btn>
                                </div>

                                <!-- Vista de cuadrícula -->
                                <v-row v-else class="px-2 py-4 pb-6">
                                    <v-col v-for="subject in filteredSubjects" :key="subject.id" cols="12" sm="6" md="4"
                                        class="pa-2">
                                        <v-card class="subject-card d-flex px-3" @click="viewSubject(subject)"
                                            style="height: 80px; position: relative; border-radius: 16px !important;"
                                            :ripple="false" flat hover>
                                            <div style="width: 60px;" class="d-flex align-center justify-center">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40"
                                                    viewBox="0 0 24 24" fill="#4DD453"
                                                    style="filter: drop-shadow(0 2px 0 #58a700);" stroke-width="1"
                                                    stroke-linecap="round" stroke-linejoin="round"
                                                    class="lucide lucide-folder">
                                                    <path
                                                        d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />
                                                </svg>
                                            </div>

                                            <div class="d-flex flex-column justify-start px-3 py-5 pr-5 my-auto"
                                                style="flex: 1; max-width: calc(100% - 80px);">
                                                <span class="font-rubik"
                                                    style="font-size: 1rem; font-weight: 500; color: #0E121B; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; text-overflow: ellipsis; line-height: 1.3; width: 100%;">
                                                    {{ subject.name }}
                                                </span>

                                                <span v-if="false && subject.num_files > 0" class="font-rubik"
                                                    style="font-size: 12px; color: #525866; margin-top: 8px;">
                                                    {{ subject.num_files }} {{ subject.num_files === 1 ? 'archivo' :
                                                        'archivos' }}
                                                </span>

                                            </div>
                                        </v-card>
                                    </v-col>

                                    <!-- Botón para agregar más asignaturas - Vista Cuadrícula -->
                                    <v-col cols="12" sm="6" md="4" class="pa-2">
                                        <v-card 
                                            class="subject-card d-flex align-center justify-center" 
                                            @click="editSubjects"
                                            style="height: 80px; position: relative; border-radius: 16px !important; border: 2px dashed #4DD453 !important; background-color: transparent !important; box-shadow: none !important;"
                                            :ripple="false" flat hover>
                                            <div class="d-flex flex-column align-center justify-center">
                                                <v-icon color="#4DD453" size="24" class="mb-1">mdi-plus</v-icon>
                                                <span class="font-rubik" style="font-size: 14px; font-weight: 500; color: #4DD453; text-align: center;">
                                                    Añadir más<br>asignaturas
                                                </span>
                                            </div>
                                        </v-card>
                                    </v-col>
                                </v-row>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-col>
            </v-row>
        </div>
    </div>
</template>

<script>
import Search from "/node_modules/lucide-vue/dist/esm/icons/search.js"

export default {
    name: 'ViewSubjects',
    components: {
        LucideSearch: Search,
    },
    props: {
        userId: {
            type: Number,
            default: null
        },
        isCommunityView: {
            type: Boolean,
            default: false
        },
        communityId: {
            type: [String, Number],
            default: null
        }
    },
    data() {
        return {
            viewMode: 'list',
            subjects: [],
            filteredSubjects: [],
            isLoading: true,
            error: null,
            searchQuery: '',
            sortOption: 'az',
            headers: [
                { text: '', value: 'icon', sortable: false, width: '30px', class: 'icon-column-table' },
                { text: 'Nombre', value: 'name', sortable: true },
                { text: 'Acciones', value: 'actions', sortable: false, align: 'center', width: '50px' }
            ],
            sortOptions: [
                { text: 'Alfabético (A-Z)', value: 'az' },
                { text: 'Alfabético (Z-A)', value: 'za' },
                { text: 'Más archivos', value: 'most_files' },
                { text: 'Menos archivos', value: 'least_files' }
            ]
        };
    },
    async mounted() {

        this.viewMode = this.$vuetify.breakpoint.xs ? 'grid' : 'list'

        await this.fetchSubjects();
    },
    watch: {
        communityId: {
            handler() {
                if (this.isCommunityView) {
                    this.fetchSubjects();
                }
            }
        },
        isCommunityView: {
            handler() {
                this.fetchSubjects();
            }
        }
    },
    methods: {
        getColorFromSubject(subject) {
            const colors = [
                "#51d244",
                "#00c473",
                "#00b29e",
                "#009dc0",
                "#0086d1",
                "#006cce"
            ];
            const index = subject.id % colors.length;
            return colors[index];
        },
        async fetchSubjects() {
            this.isLoading = true;
            this.error = null;

            try {
                let response;
                
                if (this.isCommunityView && this.communityId) {
                    // Obtener asignaturas de la comunidad
                    response = await this.axios.get('/education/subjects', {
                        headers: {
                            Authorization: this.$root.token.value
                        },
                        params: {
                            community_id: this.communityId
                        }
                    });
                } else {
                    // Obtener las asignaturas del usuario actual (comportamiento original)
                    response = await this.axios.get('/education/user-subjects', {
                        headers: {
                            Authorization: this.$root.token.value
                        },
                        params: {
                            user_id: this.userId || this.$root.currentUser.userId
                        }
                    });
                }

                if (response.data && Array.isArray(response.data)) {
                    this.subjects = response.data;
                    this.filterSubjects();
                } else {
                    this.error = 'No se pudieron cargar las asignaturas';
                }
            } catch (err) {
                console.error('Error al cargar las asignaturas:', err);
                this.error = 'Error al cargar las asignaturas. Por favor, inténtalo de nuevo.';

                this.$router.push('/app/apuntes-preguntas-flashcards/2027');
            } finally {
                this.isLoading = false;
            }
        },

        filterSubjects() {
            // Filtrar por texto de búsqueda
            let filtered = this.subjects;

            if (this.searchQuery) {
                const query = this.normalizeString(this.searchQuery.toLowerCase());
                filtered = filtered.filter(subject =>
                    this.normalizeString(subject.name.toLowerCase()).includes(query)
                );
            }

            // Ordenar según la opción seleccionada
            switch (this.sortOption) {
                case 'az':
                    filtered.sort((a, b) => a.name.localeCompare(b.name));
                    break;
                case 'za':
                    filtered.sort((a, b) => b.name.localeCompare(a.name));
                    break;
                case 'most_files':
                    filtered.sort((a, b) => (b.num_files || 0) - (a.num_files || 0));
                    break;
                case 'least_files':
                    filtered.sort((a, b) => (a.num_files || 0) - (b.num_files || 0));
                    break;
            }

            this.filteredSubjects = filtered;
        },

        // Método para normalizar texto eliminando acentos
        normalizeString(text) {
            return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        },

        viewSubject(subject) {
            // Emitir evento para mostrar los documentos de esta asignatura
            this.$emit('select-subject', subject);
        },

        async editSubjects() {
            try {
                // Primero obtenemos la información educativa del usuario
                const userId = this.userId || this.$root.currentUser.userId;
                const userInfoResponse = await this.axios.get('/profile/info', {
                    headers: {
                        Authorization: this.$root.token.value
                    },
                    params: {
                        user_id: userId
                    }
                });


                let subjects = []


                if (userInfoResponse.data) {
                    const education = userInfoResponse.data.education || {};

                    subjects = userInfoResponse.data.subjects || [];

                    // Construimos la URL con los parámetros necesarios
                    let queryParams = new URLSearchParams();

                    // Usamos valores de la educación del usuario o valores por defecto
                    queryParams.append('studyType', education.study_type_id || '4');
                    queryParams.append('center', education.center_id || '220');
                    queryParams.append('course', education.course_id || '2032');

                    if (education.university_center_id) {
                        queryParams.append('univCenter', education.university_center_id);
                    }

                    // Añadimos los IDs de las asignaturas actuales
                    if (subjects && subjects.length > 0) {
                        const subjectIds = subjects.map(s => s.id).join(',');
                        queryParams.append('subjects', subjectIds);
                    }

                    // Establecemos el paso inicial a 5 (asignaturas)
                    queryParams.append('step', 5);

                    // Redirigimos a la página de edición
                    this.$router.push('/start-3?' + queryParams.toString() + '&from=app');
                } else {
                    // Si no hay información educativa, usamos valores por defecto
                    let queryParams = new URLSearchParams();
                    queryParams.append('studyType', '4');
                    queryParams.append('center', '220');
                    queryParams.append('course', '2032');
                    queryParams.append('step', '5');

                    this.$router.push('/start-3?' + queryParams.toString() + '&from=app');
                }
            } catch (error) {
                console.error('Error al obtener información educativa:', error);
                // En caso de error, usamos valores por defecto
                let queryParams = new URLSearchParams();
                queryParams.append('studyType', '4');
                queryParams.append('center', '220');
                queryParams.append('course', '2032');
                queryParams.append('step', '5');

                this.$router.push('/start-3?' + queryParams.toString() + '&from=app');
            }
        }
    }
};
</script>

<style scoped>
.subjects-container {
    padding: 20px;
    background-color: #fbfbfb;
    flex: 1;
    max-width: 100%;
}

.subject-table-container {
    /* Mantener este contenedor vacío si no necesitas bordes o estilos específicos */
}

:deep(.icon-column-table) {
    width: 40px !important;
}

:deep(.clickable-row) {
    cursor: pointer;
    transition: background-color 0.2s;
}

:deep(.clickable-row) td:first-child {
    padding-left: 32px !important;
    padding-right: 0px !important;
}

:deep(.clickable-row:hover) {
    background-color: rgba(0, 0, 0, 0.04) !important;
}

.subject-card {
    height: 100%;
    width: 100%;
    max-width: 100%;
    transition: transform 0.2s;
    background-color: #f5f5f5;
    box-shadow: 0 3px 0 #dfdfdf !important;
}

.sort-select {
    background-color: #f0f0f0;
    border-radius: 8px;
    padding: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.search-field>>>.v-input__slot {
    background-color: #ededed6e !important;
}

.search-field>>>fieldset {
    border: 1px solid #ededed6e !important;
}

.search-field>>>label {
    font-family: -apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", Roboto, Ubuntu, sans-serif !important;
    top: 1px !important;
}

.search-field>>>input {
    padding-top: 0px !important;
}

.search-field>>>svg {
    height: 18px;
    width: 18px;
}

.select-sort>>>.v-select__selection--comma {
    margin: 0 !important;
}


.select-sort>>>fieldset {
    border: 0px !important;
}
</style>
